import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';
import { useCopy } from 'src/hooks';

import { Api } from '../../../api';
import { Text } from '../../../components';
import { Color, Style } from '../../../constants';
import TicketBadge from '../../client-tickets/components/TicketBadge';
import CardActionsMenu from './CardActionsMenu';
import CopyableText from './CopyableText';

type Account = Api.Environment.V['accounts'][number];

type Props = {
  account: Account;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onViewInstructions: () => void;
};

export default function AccountCard({
  account,
  canManage,
  onEdit,
  onDelete,
  onViewInstructions,
}: Props) {
  const { copiedKey, copy } = useCopy();

  return (
    <html.div style={[styles.card, account.isInternal && styles.cardInternal]}>
      <html.div style={[Style.row, Style.alignCenter, Style.spaceBetween]}>
        <TicketBadge
          label={account.type}
          bgColor={Color.turquoiseBg}
          textColor={Color.turquoise}
        />
        {canManage && (
          <CardActionsMenu
            items={[
              {
                key: 'edit',
                label: 'Edit',
                icon: PencilSimpleIcon,
                onClick: onEdit,
              },
              {
                key: 'delete',
                label: 'Delete',
                icon: TrashIcon,
                danger: true,
                onClick: onDelete,
              },
            ]}
          />
        )}
      </html.div>

      <Field
        label="Username"
        value={account.username}
        copied={copiedKey === 'username'}
        onCopy={() => copy('username', account.username)}
      />
      <Field
        label="Password"
        value={account.password}
        copied={copiedKey === 'password'}
        onCopy={() => copy('password', account.password)}
      />

      {(account.instructions || account.isInternal) && (
        <html.div style={[Style.row, Style.alignCenter, styles.footer]}>
          {account.instructions && (
            <html.button style={styles.viewLink} onClick={onViewInstructions}>
              <Text typeface="body.medium" color="accent">
                View Instructions
              </Text>
            </html.button>
          )}
          {account.isInternal && (
            <TicketBadge
              label="Internal"
              bgColor={Color.blueGreyBg}
              textColor={Color.blueGrey}
            />
          )}
        </html.div>
      )}
    </html.div>
  );
}

type FieldProps = {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
};

function Field({ label, value, copied, onCopy }: FieldProps) {
  return (
    <html.div>
      <Text typeface="body.small.regular" color="neutral60">
        {label}
      </Text>
      <CopyableText
        value={value}
        label={`Copy ${label.toLowerCase()}`}
        copied={copied}
        onCopy={onCopy}
      />
    </html.div>
  );
}

const styles = css.create({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    backgroundColor: color.neutral0,
    borderRadius: 12,
    padding: 20,
  },
  cardInternal: {
    backgroundColor: color.neutral10,
  },
  viewLink: {
    alignSelf: 'flex-start',
    borderWidth: 0,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
  footer: {
    gap: 12,
  },
});
